import type { Action, Agent, Hook } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { ExportOption } from '@/components/flow';
import { HOOK_EVALUATE_PREDICTION } from '@/constants';
import {
  toActionId,
  toAgentId,
  toHookId,
  useCreateAction,
  useCreateHook,
  useDeleteAction,
  useDeleteHook,
  useUpdateAgent,
} from '@/hooks/api';

export type UseAddExportOnSuccess = ({
  action,
  autoHook,
  reviewHook,
  agent,
}: {
  action: Action;
  autoHook: Hook;
  reviewHook: Hook;
  agent: Agent;
}) => void;

export type UseAddExportOnError = ({ error }: { error: Error }) => void;

export type UseAddExportCallbackArgs = {
  agentId: string;
  exportOption: ExportOption;
  onSuccess?: UseAddExportOnSuccess;
  onError?: UseAddExportOnError;
};

export type UseAddExportCallback = ({
  agentId,
  exportOption,
  onSuccess,
  onError,
}: UseAddExportCallbackArgs) => Promise<Action>;

export type UseAddExportOpts = {
  onSuccess?: UseAddExportOnSuccess;
  onError?: UseAddExportOnError;
};

export const useAddExport = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseAddExportOpts = {}) => {
  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const { mutateAsync: deleteAction } = useDeleteAction();
  const { mutateAsync: deleteHook } = useDeleteHook();

  const [error, setError] = useState<Error | null>(null);

  const addExport: UseAddExportCallback = useCallback(
    async ({ agentId, exportOption, onSuccess: onSuccessFn, onError: onErrorFn }: UseAddExportCallbackArgs) => {
      setError(null);

      const onSuccess: UseAddExportOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };

      const onError: UseAddExportOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      const cleanupFns: (() => Promise<unknown>)[] = [];

      const onCleanup = async () => {
        /* cleanup in reverse order */
        for (const fn of cleanupFns.toReversed()) {
          try {
            await fn();
          } catch (error) {
            console.error(error);
          }
        }
      };

      try {
        const action = await createAction({
          config: typeof exportOption.config === 'function' ? exportOption.config() : exportOption.config,
          functionId: exportOption.functionId,
          name: exportOption.name,
          enabled: false,
        });

        cleanupFns.push(() => deleteAction(toActionId(action.actionId)));

        const reviewHook = await createHook({
          config: {},
          trigger: 'ValidationTask has Completed',
          trueActionId: action.actionId,
        });

        cleanupFns.push(() => deleteHook(toHookId(reviewHook.hookId)));

        const autoHook = await createHook({
          functionId: HOOK_EVALUATE_PREDICTION,
          config: {},
          trigger: 'Prediction is Created',
          trueActionId: action.actionId,
        });

        cleanupFns.push(() => deleteHook(toHookId(autoHook.hookId)));

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, action.actionId, reviewHook.hookId, autoHook.hookId],
          }),
        });

        onSuccess?.({ action, reviewHook, autoHook, agent });

        return action;
      } catch (e) {
        const error = e instanceof Error ? e : new Error('An unknown error occurred');
        onCleanup().finally(() => {
          setError(error);
          onError?.({ error });
        });
        throw error;
      }
    },
    [createAction, createHook, deleteAction, deleteHook, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  const errors = useMemo(() => {
    const errors = [errorCreateAction, errorCreateHook, errorUpdateAgent, error].filter((e) => !!e);
    return errors.length > 0 ? errors : undefined;
  }, [error, errorCreateAction, errorCreateHook, errorUpdateAgent]);

  return {
    addExport,
    isPending: isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors,
  };
};
